import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from './utils';

/**
 * Card — rounded-xl container with subtle border and shadow.
 * Header and content are separated by padding only (no divider rule).
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-border bg-card text-card-foreground shadow-[0_1px_3px_hsl(220_20%_80%_/_0.3)]',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

/**
 * CardHeader — flex row so a trailing <CardAction /> sits on the right.
 * Title/description stack on the left in a column.
 */
export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-start justify-between gap-4 px-3.5 pt-2.5 pb-2',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col gap-1.5">{children}</div>
    </div>
  ),
);
CardHeader.displayName = 'CardHeader';

/**
 * CardAction — right-aligned action slot in a CardHeader
 * (buttons, menus, filters). Must be a sibling of the title/description column;
 * CardHeader renders them side-by-side via justify-between.
 */
export const CardAction = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('ml-auto flex shrink-0 items-center gap-2', className)}
      {...props}
    />
  ),
);
CardAction.displayName = 'CardAction';

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-base font-semibold leading-tight text-foreground', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-[13px] text-muted-foreground', className)}
      {...props}
    />
  ),
);
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-3.5 pb-3.5', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-end gap-2 px-4 pb-4', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';
